import { ApiChartAttribute, ApiDimension, ApiMetric } from "@incendium/api";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import {
  Autocomplete,
  Checkbox,
  GridSize,
  ListItem,
  TextField,
} from "@mui/material";
import Loading from "Components/Loading/Loading";
import { NOT_SET, useChartData } from "features/analytics";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import { useSelectedProject } from "Hooks";
import { IChart, TDimensionsValues } from "Interfaces";
import { useFromToContext } from "Providers/FromToProvider";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";
import { ColsCol, ColsLayout } from "Components/ColsLayout/ColsLayout";

type TFilterLabels = { [key in ApiDimension]: string };

interface ICampaignAnalysisAnalyzeFiltersProps {
  selectedPlatforms: string[];
  setSelectedPlatforms: (s: string[]) => void;
  selectedCampaigns: string[];
  setSelectedCampaigns: (s: string[]) => void;
  selectedGroups: string[];
  setSelectedGroups: (s: string[]) => void;
  selectedKeywords: string[];
  setSelectedKeywords: (s: string[]) => void;
  paidChannelAttributes: ApiChartAttribute[];
  disablePaidChannelAttributes: boolean;
  filterLabels?: TFilterLabels;
}

function arraysEqual(a: string[] | undefined, b: string[] | undefined) {
  return isEqual(a || [], b || []);
}

const defaultColWidth: GridSize[] = [3, 3, 3, 3];

function CampaignAnalysisAnalyzeFilters({
  selectedPlatforms,
  setSelectedPlatforms,
  selectedCampaigns,
  setSelectedCampaigns,
  selectedGroups,
  setSelectedGroups,
  selectedKeywords,
  setSelectedKeywords,
  paidChannelAttributes,
  disablePaidChannelAttributes = false,
  filterLabels,
}: ICampaignAnalysisAnalyzeFiltersProps) {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  // While a dropdown is open, edits stay local until close — avoids prop↔local loops on refresh
  const editingRef = useRef(false);
  const [colWidths, setColWidths] = useState<GridSize[]>(defaultColWidth);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(
    null,
  );

  const chart: IChart = useMemo(
    () => ({
      dimension: [
        ApiDimension.DIMENSION_SOURCE,
        ApiDimension.DIMENSION_CAMPAIGN,
        ApiDimension.DIMENSION_CONTENT,
        ApiDimension.DIMENSION_TERM,
      ],
      includeEmpty: true,
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_LEADS_COUNT],
        },
      ],
      attributes: disablePaidChannelAttributes
        ? []
        : [...paidChannelAttributes],
    }),
    [paidChannelAttributes, disablePaidChannelAttributes],
  );

  const { chartData, loading } = useChartData(
    selectedProject,
    chart,
    chartOutput,
  );

  const [selectedValues, setSelectedValues] = useState<
    Partial<TDimensionsValues>
  >({
    [ApiDimension.DIMENSION_SOURCE]: selectedPlatforms,
    [ApiDimension.DIMENSION_CAMPAIGN]: selectedCampaigns,
    [ApiDimension.DIMENSION_CONTENT]: selectedGroups,
    [ApiDimension.DIMENSION_TERM]: selectedKeywords,
  });
  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;

  // note, we include empty as its cascading and a source might not have aterm etc, so we'll remove it here
  const combinedData = useMemo(() => {
    const uniqueData = chartData?.data || [];
    return {
      [ApiDimension.DIMENSION_SOURCE]: Array.from(
        new Set(uniqueData.map((u) => u[ApiDimension.DIMENSION_SOURCE])),
      )
        .filter((a) => a !== NOT_SET)
        .sort((a, b) => `${a}`.localeCompare(`${b}`)),
      [ApiDimension.DIMENSION_CAMPAIGN]: Array.from(
        new Set(uniqueData.map((u) => u[ApiDimension.DIMENSION_CAMPAIGN])),
      )
        .filter((a) => a !== NOT_SET)
        .sort((a, b) => `${a}`.localeCompare(`${b}`)),
      [ApiDimension.DIMENSION_CONTENT]: Array.from(
        new Set(uniqueData.map((u) => u[ApiDimension.DIMENSION_CONTENT])),
      )
        .filter((a) => a !== NOT_SET)
        .sort((a, b) => `${a}`.localeCompare(`${b}`)),
      [ApiDimension.DIMENSION_TERM]: Array.from(
        new Set(uniqueData.map((u) => u[ApiDimension.DIMENSION_TERM])),
      )
        .filter((a) => a !== NOT_SET)
        .sort((a, b) => `${a}`.localeCompare(`${b}`)),
    };
  }, [chartData?.data]);

  // Pull from parent when props change and user is not mid-edit
  useUpdateEffect(() => {
    if (editingRef.current) {
      return;
    }

    setSelectedValues((prev) => {
      const next = {
        [ApiDimension.DIMENSION_SOURCE]: selectedPlatforms,
        [ApiDimension.DIMENSION_CAMPAIGN]: selectedCampaigns,
        [ApiDimension.DIMENSION_CONTENT]: selectedGroups,
        [ApiDimension.DIMENSION_TERM]: selectedKeywords,
      };
      if (
        arraysEqual(prev[ApiDimension.DIMENSION_SOURCE], selectedPlatforms) &&
        arraysEqual(prev[ApiDimension.DIMENSION_CAMPAIGN], selectedCampaigns) &&
        arraysEqual(prev[ApiDimension.DIMENSION_CONTENT], selectedGroups) &&
        arraysEqual(prev[ApiDimension.DIMENSION_TERM], selectedKeywords)
      ) {
        return prev;
      }
      return next;
    });
  }, [selectedPlatforms, selectedCampaigns, selectedGroups, selectedKeywords]);

  const commitToParent = useCallback(
    (values: Partial<TDimensionsValues>) => {
      const platforms = values[ApiDimension.DIMENSION_SOURCE] || [];
      const campaigns = values[ApiDimension.DIMENSION_CAMPAIGN] || [];
      const groups = values[ApiDimension.DIMENSION_CONTENT] || [];
      const keywords = values[ApiDimension.DIMENSION_TERM] || [];

      if (!arraysEqual(platforms, selectedPlatforms)) {
        setSelectedPlatforms(platforms);
      }
      if (!arraysEqual(campaigns, selectedCampaigns)) {
        setSelectedCampaigns(campaigns);
      }
      if (!arraysEqual(groups, selectedGroups)) {
        setSelectedGroups(groups);
      }
      if (!arraysEqual(keywords, selectedKeywords)) {
        setSelectedKeywords(keywords);
      }
    },
    [
      selectedPlatforms,
      selectedCampaigns,
      selectedGroups,
      selectedKeywords,
      setSelectedPlatforms,
      setSelectedCampaigns,
      setSelectedGroups,
      setSelectedKeywords,
    ],
  );

  const onChange = useCallback(
    (dimension: ApiDimension) => (values: string[]) => {
      setSelectedValues((prev) => {
        const next = { ...prev, [dimension]: values };
        selectedValuesRef.current = next;
        if (!editingRef.current) {
          queueMicrotask(() => commitToParent(next));
        }
        return next;
      });
    },
    [commitToParent],
  );

  useEffect(() => {
    if (selectedColumnIndex !== null) {
      const widths = [2, 2, 2, 2];
      widths[selectedColumnIndex] = 6;
      setColWidths(widths);
    } else {
      setColWidths(defaultColWidth);
    }
  }, [selectedColumnIndex]);

  const handleOpen = useCallback((index: number) => {
    setOpenIndex(null);
    setSelectedColumnIndex(null);
    editingRef.current = true;
    setSelectedColumnIndex(index);
    setTimeout(() => {
      setOpenIndex(index);
    }, 400);
  }, []);

  const handleClose = useCallback(() => {
    editingRef.current = false;
    setOpenIndex(null);
    setSelectedColumnIndex(null);
  }, []);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <ColsLayout spacing={2}>
          {[
            {
              key: ApiDimension.DIMENSION_SOURCE,
              text:
                filterLabels?.[ApiDimension.DIMENSION_SOURCE] ||
                "Select Publishers",
            },
            {
              key: ApiDimension.DIMENSION_CAMPAIGN,
              text:
                filterLabels?.[ApiDimension.DIMENSION_CAMPAIGN] ||
                "Select Campaigns",
            },
            {
              key: ApiDimension.DIMENSION_CONTENT,
              text:
                filterLabels?.[ApiDimension.DIMENSION_CONTENT] ||
                "Select Ad Groups / Sets",
            },
            {
              key: ApiDimension.DIMENSION_TERM,
              text:
                filterLabels?.[ApiDimension.DIMENSION_TERM] ||
                "Select Keywords",
            },
          ].map((o, i) => (
            <ColsCol key={o.key} width={colWidths[i]} noborder>
              <Autocomplete
                fullWidth
                options={combinedData[o.key] || []}
                multiple
                freeSolo
                open={openIndex === i}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap",
                  },
                  "&.MuiAutocomplete-root:hover .MuiAutocomplete-endAdornment .MuiButtonBase-root":
                    {
                      backgroundColor: "white",
                    },
                }}
                value={selectedValues[o.key] || []}
                disableCloseOnSelect
                disableListWrap
                onChange={(e, v) => {
                  onChange(o.key)(v);
                }}
                onClose={() => {
                  handleClose();
                  commitToParent(selectedValuesRef.current);
                }}
                onOpen={() => {
                  handleOpen(i);
                }}
                renderOption={(props, option, { selected }) => (
                  <ListItem {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      checked={selected}
                      size="small"
                      sx={{ paddingY: 0 }}
                    />
                    {option}
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={o.text} />
                )}
              />
            </ColsCol>
          ))}
        </ColsLayout>
      )}
    </>
  );
}

export default withPaidChannelAttributes(CampaignAnalysisAnalyzeFilters);
